import { http } from "./httpClient";
import { createRequestCache } from './analyticsRequestCache';
import { createCoverLoader } from './analyticsCoverCache';
import { waitForFollowerRefresh } from './followerRefreshClient';
const sessionScope = () => {
  try {
    const user = JSON.parse(localStorage.getItem('rr_user') || 'null');
    const refresh = localStorage.getItem('rr_refresh_token');
    if (!user?.id || !refresh || !localStorage.getItem('rr_access_token')) return '';
    return String(user.id);
  } catch { return ''; }
};
let browserStorage;
try { browserStorage = sessionStorage; } catch { /* unavailable */ }
export const analyticsRequests = createRequestCache(path => http.get(path), { scope: sessionScope, storage: browserStorage });
const read = path => analyticsRequests.get(path, /sync-status/.test(path) ? 1500 : /covers/.test(path) ? 300000 : /comments|updates/.test(path) ? 30000 : 60000);
export const analyticsPaths = {
  overview: id => `/analytics/?${qs({account_id:id,period:'last_month'})}`,
  media: (id,r,page,sort,dir,type) => `/analytics/media/?${qs({account_id:id,...rangeParams(r),page,sort,dir,type,page_size:20})}`,
  months: (id,r) => `/analytics/months/?${qs({account_id:id,...rangeParams(r)})}`,
  follows: (id,r) => `/analytics/follows/?${qs({account_id:id,...rangeParams(r)})}`,
  followsTotal: (id,r) => `/analytics/follows-total/?${qs({account_id:id,...rangeParams(r)})}`,
  trend: (id,r,type) => `/analytics/trend/?${qs({account_id:id,...rangeParams(r),type})}`,
  comments: (id,month,type) => `/analytics/comments/?${qs({account_id:id,month,type,view_version:3})}`,
  updates: (id,month,type) => `/analytics/updates/?${qs({account_id:id,month,type})}`,
};

const qs = (o) =>
  Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");

const rangeParams = (r) => (r.period === "custom" ? { period: r.period, from: r.from, to: r.to } : { period: r.period });
const coverLoader = createCoverLoader({
  cache: { peek: path => {
    const value = analyticsRequests.peek(path);
    return value?.expires > Date.now() ? value : null;
  }, seed: (...args) => analyticsRequests.seed(...args) },
  scope: sessionScope,
  storage: browserStorage,
  fetchBatch: (accountId, ids, retry) => {
    const path = `/analytics/covers/?${qs({ account_id: accountId, ids: ids.sort().join(","), retry: retry ? "1" : "" })}`;
    const previous = analyticsRequests.peek(path)?.data;
    return analyticsRequests.get(path, 300000, retry || !!Object.keys(previous?.errors || {}).length);
  },
});

/** Official Meta data only; null always means "unavailable" (never 0). Counts are lifetime values. */
export const analyticsApi = {
  // The individual comments of one post / Reel / Carousel (read only)
  commentsContent: (accountId, mediaId) => read(`/analytics/comments/content/?${qs({ account_id: accountId, media_id: mediaId, view_version: 3 })}`),
  // Comments: read-only monitoring (the refresh only reads from Instagram; no comment is ever posted, edited or deleted)
  comments: (accountId, month, type) => read(`/analytics/comments/?${qs({ account_id: accountId, month, type })}`),
  commentsRefresh: async (accountId, month, type) => {
    const result = await http.post(`/analytics/comments/refresh/?${qs({account_id: accountId,month,type})}`, {});
    analyticsRequests.invalidate(accountId);
    analyticsRequests.seed(analyticsPaths.comments(accountId,month,type), result);
    return result;
  },
  // Direct Messages: read-only monitoring (the refresh only reads from Instagram; no message is ever sent)
  dms: (accountId, month, status) => read(`/analytics/dms/?${qs({ account_id: accountId, month, status })}`),
  dmsRefresh: (accountId, month, status) => http.post(`/analytics/dms/refresh/?${qs({ account_id: accountId, month, status })}`, {}),
  updates: (accountId, month, type) => read(`/analytics/updates/?${qs({ account_id: accountId, month, type })}`), // Overall Content Updates (from the Calendar)
  overview: (accountId) => read(`/analytics/?${qs({ account_id: accountId, period: "last_month" })}`), // profile (current values) + sync status
  media: (accountId, range, page, sort, dir, type) => read(`/analytics/media/?${qs({ account_id: accountId, ...rangeParams(range), page, sort, dir, type, page_size: 20 })}`),
  detail: (accountId, id) => read(`/analytics/media/${id}/?${qs({ account_id: accountId })}`),
  trend: (accountId, range, type) => read(`/analytics/trend/?${qs({ account_id: accountId, ...rangeParams(range), type })}`),
  months: (accountId, range) => read(`/analytics/months/?${qs({ account_id: accountId, ...rangeParams(range) })}`),
  followsTotal: (accountId, range) => read(`/analytics/follows-total/?${qs({ account_id: accountId, ...rangeParams(range) })}`),
  followsRefresh: (accountId, range, cancelled) => waitForFollowerRefresh(
    () => http.post('/analytics/follows-refresh/', { account_id: accountId, ...rangeParams(range) }),
    () => http.get(`/analytics/follows-refresh-status/?${qs({ account_id: accountId, ...rangeParams(range) })}`),
    { cancelled }),
  follows: (accountId, range) => read(`/analytics/follows/?${qs({ account_id: accountId, ...rangeParams(range) })}`),
  compare: (accountId, p, refresh = false) => analyticsRequests.get(`/analytics/compare/?${qs({ account_id: accountId, ...p })}`, 60000, refresh),
  covers: coverLoader.load,
  cachedCover: coverLoader.peek,
  syncStatus: (accountId) => read(`/analytics/sync-status/?${qs({ account_id: accountId })}`),
  refresh: (accountId, auto = false, range = { period: "last_month" }) => http.post("/analytics/refresh/", { account_id: accountId, auto, ...rangeParams(range) }),
  exportPdf: (accountId, range) => http.getBlob(`/analytics/export/pdf/?${qs({ account_id: accountId, ...rangeParams(range) })}`),
  exportSheets: (accountId, range) => http.post("/analytics/export/sheets/", { account_id: accountId, ...rangeParams(range) }),
};
